import {tonightPasses} from '../utils/tonightPasses.js';
import {passWindow} from '../utils/observations.js';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { tierColor as passTierColor, ratingWord, PASS_WINDOW_HOURS } from '../utils/passTiers.js';

const API = import.meta.env.VITE_API_BASE || '/api';
const SPAN_H = PASS_WINDOW_HOURS;  // forward-looking pass-fetch window (hours)

const fmtTime = iso => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const fmtClock = ms => new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
// compact "9:05p" keeps labels narrow
const fmtShort = ms => {
  const d = new Date(ms); let h = d.getHours(); const m = d.getMinutes();
  const ap = h < 12 ? 'a' : 'p'; h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')}${ap}`;
};
const COMPASS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
const compass = d => d == null ? '' : COMPASS[Math.round(((d % 360) / 22.5)) % 16];

// Layout constants (px, from the plot area's bottom edge). The strip is DOTS
// on a baseline, quality by tier color, one fixed readout slot in the header,
// a small time tag on the focused dot. No per-pass text chips: labels pinned
// to a linear timeline can't survive Starlink-style clustering; the readout
// lives in reserved space, so collisions are impossible by construction.
const BASE = 18;       // baseline / where the dots sit
const MIN_SPAN_MS = 40 * 60000;  // floor so a single/tight cluster still breathes
const ZOOM_MIN = 1, ZOOM_MAX = 6, ZOOM_STEP = 1.5;

export default function TonightStrip({ observer, verdict, onFocus, focusedKey, passes:sharedPasses, loading:sharedLoading=false }) {
  const [ownPasses, setPasses] = useState([]);
  const [ownLoading, setLoading] = useState(false);
  const passes=sharedPasses ?? ownPasses, loading=sharedPasses ? sharedLoading : ownLoading;
  const scrollRef = useRef(null);   // the horizontally-scrollable viewport
  const [vw, setVw] = useState(0);  // visible viewport width
  const [zoom, setZoom] = useState(1);
  const [hoverPass, setHoverPass] = useState(null); // desktop hover → readout preview

  useEffect(() => {
    if (!observer || sharedPasses) return;
    let c = false; setLoading(true);
    fetch(`${API}/passes/tonight?lat=${observer.lat}&lon=${observer.lon}&hours=${SPAN_H}`)
      .then(r => r.json()).then(d => {
        if (c) return;
        const now = Date.now();
        setPasses(tonightPasses(d.passes,now));
        setLoading(false);
      }).catch(() => { if (!c) setLoading(false); });
    return () => { c = true; };
  }, [observer?.lat, observer?.lon, sharedPasses]);

  // Reset zoom when the location changes (the pass set changes).
  useEffect(() => { setZoom(1); }, [observer?.lat, observer?.lon]);
  // Measure the visible viewport so we can de-collide labels in pixel space.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setVw(e.contentRect.width);
    });
    ro.observe(el);
    setVw(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const now = Date.now();

  // ── Adaptive axis: fit the window to the data instead of a fixed 12h.
  const startsMs = passes.map(p => new Date(p.start).getTime());
  const firstMs = startsMs.length ? Math.min(...startsMs) : now;
  const lastMs  = startsMs.length ? Math.max(...startsMs) : now + 2 * 3600e3;
  let span0 = firstMs, span1 = lastMs;
  if (span1 - span0 < MIN_SPAN_MS) {
    const mid = (span0 + span1) / 2;
    span0 = mid - MIN_SPAN_MS / 2; span1 = mid + MIN_SPAN_MS / 2;
  }
  const pad = Math.max(8 * 60000, (span1 - span0) * 0.08);
  span0 -= pad; span1 += pad;
  const spanMs = span1 - span0;
  const xOf = ms => Math.max(0, Math.min(1, (ms - span0) / spanMs));

  // Zoom widens the inner content beyond the viewport; the viewport scrolls.
  const innerW = Math.max(vw, vw * zoom);

  // axis ticks at "nice" clock intervals across the visible span (denser when zoomed)
  const spanH = (spanMs / 3600e3) / zoom;
  const stepMin = spanH <= 1 ? 15 : spanH <= 2 ? 30 : spanH <= 4 ? 60 : spanH <= 8 ? 120 : 180;
  const stepMs = stepMin * 60000;
  const ticks = [];
  for (let t = Math.ceil(span0 / stepMs) * stepMs; t <= span1; t += stepMs) ticks.push(t);
  const nowInSpan = now >= span0 && now <= span1;

  // Lay out dots left→right; near-coincident dots get a small vertical lift so
  // every pass stays individually tappable inside a burst.
  const laid = [];
  let focusedCx = null;
  if (innerW > 0) {
    let prevCx = -Infinity, lifted = false;
    for (const p of passes) {
      const cx = xOf(new Date(p.start).getTime()) * innerW;
      lifted = (cx - prevCx < 10) ? !lifted : false;
      prevCx = cx;
      const isFocused = focusedKey === `${p.norad_id}:${p.start}`;
      if (isFocused) focusedCx = cx;
      laid.push({ p, cx, lift: lifted ? 9 : 0, isFocused });
    }
  }

  // Keep the selected pass in view when zoom changes.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || focusedCx == null || innerW <= vw) return;
    const target = focusedCx - vw / 2;
    el.scrollTo({ left: Math.max(0, Math.min(innerW - vw, target)), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth' });
  }, [zoom, focusedKey, innerW]);  // eslint-disable-line react-hooks/exhaustive-deps

  const zoomBtn = (label, onClick, disabled) => (
    <button onClick={onClick} disabled={disabled} title={label === '+' ? 'Zoom in' : label === '–' ? 'Zoom out' : 'Fit all'}
      style={{
        fontFamily: 'var(--mono)', fontSize: 11, lineHeight: 1, width: label === 'fit' ? 'auto' : 20, height: 20,
        padding: label === 'fit' ? '0 7px' : 0, cursor: disabled ? 'default' : 'pointer',
        background: 'transparent', color: disabled ? 'var(--txt-mute)' : 'var(--txt-dim)',
        border: '1px solid var(--hair-2)', borderRadius: 3, opacity: disabled ? 0.5 : 1,
      }}>{label}</button>
  );

  const focusedPass = passes.find(p => `${p.norad_id}:${p.start}` === focusedKey) || null;
  const shown = hoverPass || focusedPass;   // one label, fixed slot, always readable

  return (
    <div style={{ height: '100%', background: 'var(--bg)', borderTop: '1px solid var(--hair)', padding: '8px 40px 10px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', flexWrap:'wrap', alignItems: 'center', gap: 8, marginBottom: 6, minWidth: 0 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em', color: 'var(--accent)', flexShrink: 0 }}>TONIGHT</span>
        {passes.length > 0 && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--ui-small)', color: 'var(--txt-dim)', flex:'1 1 100%', letterSpacing: '0.02em' }}>
            {passWindow({start:new Date(firstMs).toISOString(),end:new Date(lastMs).toISOString()},'Timeline window')}
          </span>
        )}
        {/* Fixed readout, the ONLY pass text in the strip. Hover previews
            (desktop) without stealing focus; otherwise the focused pass. */}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--txt-bright)', letterSpacing: '0.03em' }}>
          {shown && (<>
            {shown.name}
            <span style={{ color: 'var(--txt-dim)' }}>
              {' · '}{fmtShort(new Date(shown.start).getTime())} · peak {Math.round(shown.max_el || 0)}° {compass(shown.azimuth_max)}{shown.approx_magnitude != null ? ` · m${(+shown.approx_magnitude).toFixed(1)}` : ''}{' · '}
            </span>
            <span style={{ color: passTierColor(shown.visibility_tier || 0), fontWeight: 700, letterSpacing: '0.08em' }}>{ratingWord(shown)}</span>
          </>)}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {zoomBtn('–', () => setZoom(z => Math.max(ZOOM_MIN, +(z / ZOOM_STEP).toFixed(2))), zoom <= ZOOM_MIN)}
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--txt-dim)', minWidth: 30, textAlign: 'center' }}>{zoom.toFixed(1)}×</span>
            {zoomBtn('+', () => setZoom(z => Math.min(ZOOM_MAX, +(z * ZOOM_STEP).toFixed(2))), zoom >= ZOOM_MAX)}
            {zoom > 1 && zoomBtn('fit', () => setZoom(1), false)}
          </div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--txt-dim)' }}>
            {loading ? 'loading…' : `${passes.length} pass${passes.length === 1 ? '' : 'es'}`}
          </span>
        </div>
      </div>

      {/* horizontally-scrollable viewport; inner content widens with zoom */}
      <div ref={scrollRef} style={{ position: 'relative', flex: 1, minHeight: 0, overflowX: zoom > 1 ? 'auto' : 'hidden', overflowY: 'hidden' }}>
        <div style={{ position: 'relative', width: innerW || '100%', height: '100%' }}>
          {/* baseline */}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: BASE, height: 1, background: 'var(--hair-2)' }} />
          {/* axis ticks */}
          {ticks.map(t => (
            <div key={t} style={{ position: 'absolute', bottom: 0, left: `${xOf(t) * 100}%`, transform: 'translateX(-50%)', textAlign: 'center' }}>
              <div style={{ width: 1, height: 5, background: 'var(--hair-2)', margin: '0 auto 3px' }} />
              <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--txt-dim)', whiteSpace: 'nowrap' }}>{fmtClock(t)}</span>
            </div>
          ))}
          {/* "now" marker */}
          {nowInSpan && (
            <div style={{ position: 'absolute', bottom: BASE, top: 0, left: `${xOf(now) * 100}%`, width: 1, background: 'var(--hair)', transform: 'translateX(-50%)' }}>
              <span style={{ position: 'absolute', bottom: -1, left: 3, fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--txt-mute)' }}>now</span>
            </div>
          )}

          {/* markers: tier-colored dots on the baseline; the focused dot carries
              a small time tag just above it (below would collide with the axis). */}
          {laid.map(({ p, cx, lift, isFocused }) => {
            const tier = p.visibility_tier || 0, color = passTierColor(tier);
            const dim = focusedKey && !isFocused;
            const tint = (pct) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;
            return (
              <div key={`${p.norad_id}:${p.start}`} style={{ opacity: dim ? 0.65 : 1, transition: 'opacity 150ms' }}>
                <button onClick={() => onFocus?.(p)}
                  onMouseEnter={() => setHoverPass(p)} onMouseLeave={() => setHoverPass(null)}
                  aria-pressed={isFocused} aria-label={`${p.name} · ${passWindow(p)} · ${ratingWord(p)}`} title={`${p.name} · ${passWindow(p)} · max ${p.max_el?.toFixed(0)}° · m${(p.approx_magnitude ?? 0).toFixed(1)} · ${ratingWord(p)} · tap to plot`}
                  style={{ position: 'absolute', left: cx, bottom: BASE + lift, transform: 'translate(-50%, 50%)', background: 'none', border: 'none', padding: 6, cursor: 'pointer', lineHeight: 0 }}>
                  <span style={{ display: 'block', width: isFocused ? 15 : 10, height: isFocused ? 15 : 10, borderRadius: '50%', background: color, boxShadow: isFocused ? `0 0 12px ${color}` : `0 0 4px ${tint(80)}`, border: isFocused ? '2px solid var(--bg)' : 'none', transition: 'width 120ms, height 120ms' }} />
                </button>
                {isFocused && (
                  <span style={{ position: 'absolute', left: cx, bottom: BASE + lift + 13, transform: 'translateX(-50%)', fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, color, letterSpacing: '0.04em', whiteSpace: 'nowrap', pointerEvents: 'none', textShadow: '0 0 6px rgba(0,0,0,0.9)' }}>
                    {fmtShort(new Date(p.start).getTime())}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {!loading && passes.length === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--txt-dim)' }}>
            No visible passes in the next {SPAN_H} hours.
          </div>
        )}
      </div>
    </div>
  );
}
